import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useWarmInstrumentDisplayData } from '~/components/root/use-warm-instrument-display-data.ts';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import { WatchlistNavbar } from '~/components/watchlist/watchlist-navbar.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import type { WatchlistPageData } from '~/server/watchlist/get-watchlist-page-data.ts';
import { getWatchlistPageDataQueryOptions } from '~/server/watchlist/watchlist-page-data-query.ts';
import { cn } from '~/utils/cn.ts';
import { getWatchlistInstrumentDisplayTickers } from '~/utils/instrument-display-tickers.ts';

export const Route = createFileRoute('/watchlists/$watchlistIndex')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: WatchlistsLayoutPage,
	loader: ({ context, params }): Promise<WatchlistPageData> =>
		context.queryClient.ensureQueryData(
			getWatchlistPageDataQueryOptions({ watchlistIndex: Number(params.watchlistIndex) }),
		),
	pendingComponent: WatchlistsLayoutPending,
	pendingMinMs: 250,
});

function WatchlistsLayoutPending() {
	return (
		<>
			<div className="pr-1 pl-1 sm:pr-3">
				<Skeleton className="h-12 w-full rounded-md border border-border bg-card" />
			</div>
			<div className="pr-1 pl-1 sm:pr-3">
				<Skeleton className="h-watchlist-body-height w-full rounded-md border border-border bg-card" />
			</div>
		</>
	);
}

function WatchlistsLayoutPage() {
	const t = useTranslations();
	const { watchlistIndex } = Route.useParams();
	const { data: loaderData } = useSuspenseQuery(
		getWatchlistPageDataQueryOptions({ watchlistIndex: Number(watchlistIndex) }),
	);
	const navigate = Route.useNavigate();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const activeIndex = loaderData.watchlistIndex;
	const activeWatchlist = loaderData.watchlists[activeIndex];

	useWarmInstrumentDisplayData(getWatchlistInstrumentDisplayTickers(activeWatchlist));

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.ctrlKey && !Number.isNaN(Number(event.key))) {
				const watchlistIndex = Number(event.key) - 1;
				if (loaderData.watchlists?.[watchlistIndex]) {
					event.preventDefault();
					void navigate({
						params: {
							watchlistIndex: String(watchlistIndex),
						},
						to: '/watchlists/$watchlistIndex/overview',
					});
				}
			}
		}

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [loaderData.watchlists, navigate]);

	if (pathname === `/watchlists/${activeIndex}` && loaderData.watchlists.length > 0) {
		return (
			<Navigate
				params={{
					watchlistIndex: String(activeIndex),
				}}
				to="/watchlists/$watchlistIndex/overview"
			/>
		);
	}

	return (
		<>
			<div className="pr-1 pl-1 sm:pr-3">
				<WatchlistNavbar activeIndex={activeIndex} watchlists={loaderData.watchlists} />
			</div>
			{loaderData.watchlists.length === 0 ? (
				<div className="flex flex-row justify-end gap-x-1 p-2">
					<p className="text-foreground italic">{t('watchlist.empty')}</p>
					<span>😊</span>
				</div>
			) : (
				<ScrollArea
					classNames={{
						root: cn('flex h-watchlist-body-height w-full flex-col pr-1 pl-1 sm:pr-3'),
						viewport: cn('h-watchlist-body-height w-full'),
					}}
				>
					<Outlet />
				</ScrollArea>
			)}
		</>
	);
}
